import React, { useState, useEffect, useCallback, useRef } from "react";
import { auth } from "../../Services/Firebase/FirebaseIndex";
import { sendVerificationEmail } from "../../Services/Firebase/FirebaseModules/firebaseAuthService";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { retryHandler } from "../../Services/Firebase/FirebaseUtils/FirebaseRetryHandler";
import GigifyLogo from "../../assets/Logo/GIGiFY Logo Horizontal no tagline.png";
import BandCrowdBg from '../../assets/Images/bandcrowd2.jpg';
import debug from '../../utils/debug';

// Configuration
const POLLING_INTERVAL = 10000; // 10 seconds
const ENABLE_AUTO_POLLING = true;

// Color Palette
const colors = {
  darkBlue: "#002244",
  mediumBlue: "#144874",
  brightBlue: "#01a2f1",
  orange: "#f85008",
  orangeHover: "#e6470a",
  success: "#00d084",
  successLight: "rgba(0, 208, 132, 0.1)",
  error: "#ff4444",
  errorLight: "rgba(255, 68, 68, 0.1)",
  warning: "#ff9800",
  white: "#fff",
  lightGray: "#f8f9fa",
  mediumGray: "#666",
  darkGray: "#999",
  borderGray: "#e0e0e0",
};

const EmailVerification = () => {
  const { refreshUser, userType: contextUserType, currentUser } = useAuth();
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkAttempts, setCheckAttempts] = useState(0);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [isAutoChecking, setIsAutoChecking] = useState(ENABLE_AUTO_POLLING);

  const checkIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const isMountedRef = useRef(true);

  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const userType = queryParams.get("type") || "Talent";
  const userUID = queryParams.get("userUID") || currentUser?.uid;

  // Helper function to get correct user type from Firestore
  const getUserTypeFromFirestore = useCallback(async (uid) => {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../Services/Firebase/FirebaseIndex');
      const userDoc = await getDoc(doc(db, "Users", uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userType = userData?.profile?.userType;
        debug.log('EmailVerification', 'User type from Firestore:', { userType, uid });
        if (userType && typeof userType === 'string') {
          const normalizedType = userType.toLowerCase();
          if (normalizedType === 'talent') return 'Talent';
          if (normalizedType === 'organiser' || normalizedType === 'organizer') return 'Organiser';
        }
      }
      debug.warn('EmailVerification', 'Could not determine user type, defaulting to Talent');
      return 'Talent';
    } catch (error) {
      debug.error('EmailVerification', 'Error fetching user type from Firestore', error);
      return 'Talent';
    }
  }, []);

  // Component lifecycle
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Verification check function
  const checkVerification = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        debug.log('EmailVerification', 'No current user found');
        return false;
      }

      await user.reload();
      
      debug.log('EmailVerification', 'Checking verification status', {
        emailVerified: user.emailVerified,
        uid: user.uid,
        attempt: checkAttempts + 1
      });

      if (user.emailVerified) {
        setIsVerifying(true);
        setVerificationProgress(100);

        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }

        await refreshUser();

        const correctUserType = await getUserTypeFromFirestore(user.uid);
        const destination = `/${correctUserType.toLowerCase()}-dashboard/${user.uid}`;

        debug.log('EmailVerification', 'Email verified, navigating', { destination, correctUserType });

        setTimeout(() => {
          navigate(destination, { replace: true });
        }, 500);

        return true;
      }

      setCheckAttempts(prev => prev + 1);
      return false;

    } catch (err) {
      debug.error('EmailVerification', 'Error checking verification', err);
      return false;
    }
  }, [navigate, refreshUser, getUserTypeFromFirestore, checkAttempts]);

  // Set up verification checking with polling
  useEffect(() => {
    if (!userUID) {
      debug.error('EmailVerification', 'Missing userUID');
      navigate("/register");
      return;
    }

    if (currentUser?.emailVerified) {
      debug.log('EmailVerification', 'User already verified, redirecting');
      getUserTypeFromFirestore(currentUser.uid).then(correctUserType => {
        const destination = `/${correctUserType.toLowerCase()}-dashboard/${currentUser.uid}`;
        navigate(destination, { replace: true });
      });
      return;
    }

    let progress = 0;
    progressIntervalRef.current = setInterval(() => {
      if (progress < 90 && isMountedRef.current) {
        progress += 0.5;
        setVerificationProgress(progress);
      }
    }, 200);

    const performCheck = async () => {
      if (!isMountedRef.current || !isAutoChecking) return;
      await checkVerification();
    };

    setTimeout(performCheck, 1000);

    if (isAutoChecking) {
      checkIntervalRef.current = setInterval(performCheck, POLLING_INTERVAL);
      debug.log('EmailVerification', `Auto-checking enabled with ${POLLING_INTERVAL/1000}s interval`);
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [userUID, currentUser?.emailVerified, isAutoChecking, checkVerification, navigate, getUserTypeFromFirestore]);

  // Resend verification email
  const resendVerification = async () => {
    try {
      debug.firebase('EmailVerification', 'Resending verification email');
      setIsLoading(true);
      setError(null);

      const result = await retryHandler.execute(async () => {
        return await sendVerificationEmail();
      }, {
        context: 'EmailVerification.resendVerification',
        maxAttempts: 3
      });

      debug.log('EmailVerification', 'Resend result:', result);

      if (result && result.success) {
        setIsSent(true);
        debug.log('EmailVerification', 'Verification email resent successfully');

        setVerificationProgress(0);
        setCheckAttempts(0);
        setIsAutoChecking(true);

        setTimeout(() => setIsSent(false), 5000);
      } else {
        throw new Error(result?.error || "Failed to send verification email");
      }
    } catch (err) {
      debug.error('EmailVerification', 'Resend verification error', err);
      setError(`Failed to resend: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Manual verification check
  const manualCheck = async () => {
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError("No user logged in. Please refresh the page.");
        return;
      }

      debug.log('EmailVerification', 'Manual verification check started');

      await user.reload();
      await refreshUser();

      debug.log('EmailVerification', 'Manual check - user reloaded', {
        isVerified: user.emailVerified
      });

      if (user.emailVerified) {
        setIsVerifying(true);
        setVerificationProgress(100);

        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }

        const correctUserType = await getUserTypeFromFirestore(user.uid);
        const destination = `/${correctUserType.toLowerCase()}-dashboard/${user.uid}`;

        debug.log('EmailVerification', 'Manual check successful - redirecting', { destination });

        setTimeout(() => {
          navigate(destination, { replace: true });
        }, 1000);
      } else {
        setError("Email not verified yet. Please check your email and click the verification link.");
        setCheckAttempts(prev => prev + 1);
      }

    } catch (error) {
      debug.error('EmailVerification', 'Manual check error', error);
      setError("Error checking verification status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };


  // Loading state
  if (!currentUser || !userUID) {
    return (
      <div className="email-verification-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        .email-verification-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.mediumBlue} 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          font-family: Montserrat, sans-serif;
          z-index: 9999;
        }

        .verification-card {
          background-color: ${colors.white};
          border-radius: 15px;
          border: 1px solid ${colors.mediumBlue};
          padding: 0;
          width: 100%;
          max-width: 400px;
          box-shadow:
            0 50px 100px rgba(0, 0, 0, 0.4),
            0 25px 50px rgba(0, 0, 0, 0.25),
            0 12px 24px rgba(0, 0, 0, 0.15),
            0 6px 12px rgba(0, 0, 0, 0.1),
            0 2px 4px rgba(0, 0, 0, 0.05);
          position: relative;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          transform: translateY(-8px);
        }

        .verification-card:hover {
          transform: translateY(-16px);
          box-shadow:
            0 80px 160px rgba(0, 0, 0, 0.5),
            0 40px 80px rgba(0, 0, 0, 0.35),
            0 20px 40px rgba(0, 0, 0, 0.25),
            0 10px 20px rgba(0, 0, 0, 0.15),
            0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .logo-header {
          background-image: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${BandCrowdBg});
          background-size: cover;
          background-position: center;
          padding: 1rem;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 15px 15px 0 0;
          position: relative;
          top: 0;
          left: 0;
          right: 0;
          margin: 0;
        }

        .content-area {
          padding: 1.5rem;
        }

        .progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background-color: rgba(1, 162, 241, 0.1);
        }

        .progress-fill {
          height: 100%;
          background-color: ${colors.orange};
          transition: width 0.3s ease;
        }

        /* Compact button styles */
        .btn {
          padding: 0.65rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 15px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          outline: none;
          position: relative;
          overflow: hidden;
        }

        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .btn-primary {
          background-color: ${colors.orange};
          color: ${colors.white};
          width: 100%;
          border: 1px solid ${colors.orange};
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1), 0 15px 35px rgba(0, 0, 0, 0.15);
        }

        .btn-primary:hover:not(:disabled) {
          background-color: ${colors.orangeHover};
          transform: translateY(-4px);
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15), 0 25px 50px rgba(0, 0, 0, 0.2);
        }

        .btn-primary:active:not(:disabled) {
          transform: translateY(-2px) scale(0.98);
        }

        .btn-secondary {
          width: 100%;
          background-color: transparent;
          color: ${colors.brightBlue};
          border: 2px solid ${colors.brightBlue};
          font-size: 0.9rem;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(1, 162, 241, 0.15);
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: ${colors.brightBlue};
          color: ${colors.white};
          transform: translateY(-3px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15), 0 16px 35px rgba(1, 162, 241, 0.25);
        }

        .btn-secondary:active:not(:disabled) {
          transform: translateY(-1px) scale(0.98);
        }


        /* Status indicator */
        .status-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }

        .status-indicator.active {
          background-color: ${colors.success};
          animation: pulse 1.5s ease-in-out infinite;
        }

        .status-indicator.inactive {
          background-color: ${colors.warning};
        }

        /* Icon container */
        .icon-container {
          font-size: 2.5rem;
          margin-bottom: 0.75rem;
        }

        .icon-container:not(.verifying) {
          animation: pulse 2s ease-in-out infinite;
        }

        /* Loading spinner */
        .spinner {
          width: 30px;
          height: 30px;
          border: 3px solid rgba(1, 162, 241, 0.2);
          border-top: 3px solid ${colors.brightBlue};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(255, 255, 255, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 15px;
          z-index: 10;
        }

        /* Animations */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(0.95); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }


        /* Compact info and message boxes */
        .info-box {
          background-color: rgba(1, 162, 241, 0.05);
          border-radius: 15px;
          padding: 0.75rem;
          margin-bottom: 1rem;
          border: 1px solid #e5e7eb;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.12);
        }

        .success-message {
          background-color: ${colors.successLight};
          color: #00a86b;
          border-radius: 15px;
          padding: 0.75rem;
          margin-bottom: 0.75rem;
          font-size: 0.85rem;
          border: 1px solid #e5e7eb;
          box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1), 0 6px 15px rgba(0, 0, 0, 0.08);
          animation: fadeIn 0.3s ease;
        }

        .error-message {
          background-color: ${colors.errorLight};
          color: ${colors.error};
          border-radius: 15px;
          padding: 0.75rem;
          margin-bottom: 0.75rem;
          font-size: 0.85rem;
          border: 1px solid #e5e7eb;
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1), 0 12px 25px rgba(0, 0, 0, 0.12);
          animation: fadeIn 0.3s ease;
        }

        .tips-box {
          background-color: ${colors.lightGray};
          border-radius: 15px;
          padding: 0.75rem;
          margin-bottom: 1rem;
          font-size: 0.85rem;
          color: ${colors.mediumGray};
          line-height: 1.4;
          border: 1px solid #e5e7eb;
          box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1), 0 6px 15px rgba(0, 0, 0, 0.08);
        }

        .tips-box ul {
          margin: 0.25rem 0 0 0;
          padding-left: 1.25rem;
        }

        .tips-box li {
          margin: 0.2rem 0;
        }

        /* Mobile responsiveness */
        @media (max-width: 480px) {
          .verification-card {
            max-width: 95%;
          }
          
          .content-area {
            padding: 1.25rem;
          }
        }
      `}</style>

      <div className="email-verification-container">
        <div className="verification-card">
          {/* Progress bar */}
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${verificationProgress}%` }} />
          </div>

          {/* Logo Header */}
          <div className="logo-header">
            <img
              src={GigifyLogo}
              alt="GIGiFY"
              style={{ height: "40px", width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)" }}
            />
          </div>

          {/* Content Area */}
          <div className="content-area">
            <div style={{ textAlign: "center" }}>
              <div className={`icon-container ${isVerifying ? 'verifying' : ''}`}>
                {isVerifying ? "✅" : "📧"}
              </div>

              <h2 style={{ color: colors.mediumBlue, fontSize: "1.5rem", marginBottom: "0.75rem", fontWeight: "600", margin: "0 0 0.75rem 0" }}>
                {isVerifying ? "Email Verified!" : "Verify Your Email"}
              </h2>

              {!isVerifying && (
                <div className="info-box">
                  <p style={{ color: colors.mediumBlue, fontSize: "0.9rem", lineHeight: "1.4", margin: "0 0 0.5rem 0" }}>
                    We've sent a verification email to your inbox.
                  </p>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", fontSize: "0.8rem", color: colors.mediumGray }}>
                    <span className={`status-indicator ${isAutoChecking ? 'active' : 'inactive'}`} />
                    <span>
                      {isAutoChecking 
                        ? `Checking every ${POLLING_INTERVAL/1000}s` 
                        : "Auto-check paused"}
                    </span>
                  </div>
                </div>
              )}

              {!isVerifying && (
                <div className="tips-box">
                  <strong>Quick Tips:</strong>
                  <ul>
                    <li>Check spam/junk folder</li>
                    <li>Click the link in email</li>
                    <li>Auto-detects verification</li>
                  </ul>
                </div>
              )}

              {isSent && (
                <div className="success-message">
                  ✅ Verification email sent!
                </div>
              )}

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {!isVerifying && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <button
                    className="btn btn-primary"
                    onClick={resendVerification}
                    disabled={isLoading || isSent}
                  >
                    {isLoading ? "Sending..." : isSent ? "Email Sent ✓" : "Resend Verification Email"}
                  </button>

                  <button
                    className="btn btn-secondary"
                    onClick={manualCheck}
                    disabled={isLoading}
                    style={{ marginTop: "0.5rem" }}
                  >
                    {isLoading ? "Checking..." : "Refresh"}
                  </button>
                </div>
              )}

            </div>
          </div>

          {isLoading && (
            <div className="loading-overlay">
              <div className="spinner" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmailVerification;